/**
 * PriceSummaryBar Component
 *
 * MVP 1.22 - Experience Pricing & Promotions Engine
 * Story: SCRUM-373 - Understand Price Breakdown
 *
 * Mobile sticky price bar that stays visible at all scroll positions.
 *
 * Acceptance Criteria:
 * - AC-002.8: Mobile sticky bar with total and "Book Now" visible at all scroll positions
 */

import React from 'react';
import PropTypes from 'prop-types';
import { formatCentsToDollars } from '../../utils/formHelpers';

/**
 * Skeleton loader for price bar
 */
const PriceBarSkeleton = () => (
  <div className="flex items-center justify-between animate-pulse">
    <div className="space-y-1">
      <div className="h-4 bg-gray-300 rounded w-20"></div>
      <div className="h-6 bg-gray-300 rounded w-24"></div>
    </div>
    <div className="h-10 bg-gray-300 rounded w-28"></div>
  </div>
);

/**
 * PriceSummaryBar Component
 *
 * Sticky footer bar for mobile showing total price and Book Now button.
 */
const PriceSummaryBar = ({
  totalCents,
  pricePerPersonCents,
  partySize = 2,
  onBookNow,
  loading = false,
  bookNowLabel = 'Book Now',
  disabled = false,
  className = ''
}) => {
  return (
    <div
      className={`
        fixed bottom-0 left-0 right-0 z-50
        bg-white border-t border-gray-200 shadow-lg
        px-4 py-3 safe-area-inset-bottom
        md:hidden
        ${className}
      `}
    >
      {loading ? (
        <PriceBarSkeleton />
      ) : (
        <div className="flex items-center justify-between">
          {/* Price display */}
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">Total price</span>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-gray-900">
                {formatCentsToDollars(totalCents)}
              </span>
              {partySize > 1 && pricePerPersonCents && (
                <span className="text-xs text-gray-500">
                  ({formatCentsToDollars(pricePerPersonCents)}/person)
                </span>
              )}
            </div>
          </div>

          {/* Book Now button */}
          <button
            type="button"
            onClick={onBookNow}
            disabled={disabled || loading}
            className={`
              px-6 py-2.5 rounded-lg font-semibold text-white
              transition-colors
              ${disabled
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 active:bg-green-800'}
            `}
          >
            {bookNowLabel}
          </button>
        </div>
      )}
    </div>
  );
};

PriceSummaryBar.propTypes = {
  totalCents: PropTypes.number,
  pricePerPersonCents: PropTypes.number,
  partySize: PropTypes.number,
  onBookNow: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  bookNowLabel: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string
};

/**
 * Spacer component to prevent content from being hidden behind the sticky bar
 */
export const PriceSummaryBarSpacer = () => (
  <div className="h-20 md:hidden" aria-hidden="true" />
);

export default PriceSummaryBar;
